import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type MyHostEventSummary = {
  id: string;
  slug: string | null;
  name: string;
  date: string;
  time: string;
  endTime: string | null;
  coverImageUrl: string | null;
  reviewStatus: string;
  visibility: string;
  venueName: string;
  rsvpCount: number;
};

export function useMyHostEvents() {
  return useQuery({
    queryKey: ["myHostEvents"],
    queryFn: async () => api.get<MyHostEventSummary[]>("/host/events"),
  });
}
import { api } from "@/lib/api";
import type { EventLink } from "@shared/eventLinks";

export type CreateHostEventRequest = {
  // Admin-only: link the event to a club as its in-person meetup.
  clubId?: string | null;
  groupId?: string | null;
  name: string;
  description?: string;
  date: string;
  time: string;
  endTime?: string | null;
  cityId: string;
  venueName: string;
  venueAddress: string;
  venueType?: string;
  venueLat?: number | null;
  venueLng?: number | null;
  venuePlaceId?: string;
  capacity?: number | null;
  // null = not specified, 0 = free, >0 = price in cents.
  priceCents?: number | null;
  types?: string[];
  visibility?: "public" | "unlisted";
  audienceFilter?: {
    ageMin?: number;
    ageMax?: number;
    genders?: string[];
    orientations?: string[];
  };
  addressRevealPolicy?: "immediately" | "on_rsvp" | "hours_before";
  addressRevealHoursBefore?: number;
  coverImageUrl?: string;
  links?: EventLink[];
};

export type CreateHostEventResponse = {
  id: string;
  slug: string;
  shareUrl: string;
};

export function useCreateHostEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateHostEventRequest) =>
      api.post<CreateHostEventResponse>(`/host/events`, body),
    onSuccess: () => {
      // The new event should show up in the listing immediately.
      qc.invalidateQueries({ queryKey: ["eventsList"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Owner-only fetch + update, used by the edit flow.
// ---------------------------------------------------------------------------

export type HostEventDetail = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  date: string;
  time: string;
  endTime: string | null;
  capacity: number | null;
  priceCents: number | null;
  types: string[];
  visibility: "public" | "unlisted";
  audienceFilter: {
    ageMin?: number;
    ageMax?: number;
    genders?: string[];
    orientations?: string[];
  } | null;
  coverImageUrl: string | null;
  links: EventLink[];
  groupId: string | null;
  addressRevealPolicy: "immediately" | "on_rsvp" | "hours_before";
  addressRevealHoursBefore: number | null;
  city: { id: string; name: string } | null;
  venue: {
    name: string;
    address: string;
    lat: number | null;
    lng: number | null;
    placeId: string | null;
    type: string;
  };
};

export function useHostEvent(slug: string | null) {
  return useQuery({
    queryKey: ["hostEvent", slug],
    queryFn: async () =>
      api.get<HostEventDetail>(`/host/events/${slug}`),
    enabled: !!slug,
    retry: false,
  });
}

export function useUpdateHostEvent(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateHostEventRequest) =>
      api.patch<CreateHostEventResponse>(`/host/events/${slug}`, body),
    onSuccess: () => {
      // Bust caches for the public page, the listing, and the owner-only
      // edit-form fetch so subsequent reads pick up the new values.
      qc.invalidateQueries({ queryKey: ["eventPublic", slug] });
      qc.invalidateQueries({ queryKey: ["hostEvent", slug] });
      qc.invalidateQueries({ queryKey: ["eventsList"] });
    },
  });
}
